'use client'

import { PanelLeftCloseIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { AccountConnectionWizard } from '@/features/dashboard/components/account-connection/account-connection-wizard'
import { ACCOUNT_CONNECTION_SHEET_CLASS } from '@/features/dashboard/components/account-connection/styles'
import { type PublicAccount } from '@/types/generated/common/account'
import type { TradeVenue } from '@/types/generated/common/types'

type AccountConnectionSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onComplete?: (account: PublicAccount) => void
  /** Pre-selects the venue on the exchange-select step — e.g. resuming a
   * half-onboarded account: `findResumableAgentWalletAccount` picks it back
   * up once its venue is preselected here, no need to duplicate the connect
   * flow at the call site. */
  initialVenue?: TradeVenue
}

export function AccountConnectionSheet({
  open,
  onOpenChange,
  onComplete,
  initialVenue,
}: AccountConnectionSheetProps) {
  const t = useTranslations('accountConnection')
  const router = useRouter()
  const pathname = usePathname()
  const [popoverContainer, setPopoverContainer] = useState<Element | null>(null)

  // Lets the full-page wizard know it was opened from a trade page so it can
  // show a "Back" CTA instead of "Use another account" / logout.
  const onboardingHref = `/onboarding?from=${encodeURIComponent(pathname)}`

  const handleExpand = () => {
    router.push(onboardingHref)
  }

  useEffect(() => {
    if (open) router.prefetch(onboardingHref)
  }, [open, router, onboardingHref])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={setPopoverContainer}
        side="right"
        className={ACCOUNT_CONNECTION_SHEET_CLASS}
        onInteractOutside={(event) => {
          // Reown AppKit's wallet picker (<w3m-modal>) is portaled to
          // document.body, outside this sheet's DOM subtree. Clicking inside
          // it — e.g. the connector search field during a Derive connect —
          // otherwise registers as an "outside" interaction and dismisses the
          // sheet mid-flow, unmounting the wizard while the wallet-connect
          // promise it's awaiting keeps running in a detached closure.
          // Shadow-DOM retargeting means the event target resolves to the
          // <w3m-modal> host element itself, so `closest` is reliable here.
          if (event.target instanceof Element && event.target.closest('w3m-modal')) {
            event.preventDefault()
          }
        }}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{t('sheetTitle')}</SheetTitle>
          <SheetDescription>{t('sheetDescription')}</SheetDescription>
        </SheetHeader>
        <AccountConnectionWizard
          variant="sheet"
          popoverContainer={popoverContainer}
          initialVenue={initialVenue}
          headerLeading={
            <button
              type="button"
              onClick={handleExpand}
              onFocus={() => router.prefetch(onboardingHref)}
              onPointerEnter={() => router.prefetch(onboardingHref)}
              className="flex cursor-pointer items-center gap-2 border border-white/10 bg-white/5 px-1.5 py-1.5 transition-colors outline-none hover:bg-white/10"
              title={t('expandToFullPage')}
              aria-label={t('expandToOnboardingPage')}
            >
              <PanelLeftCloseIcon className="size-4 text-white/60" />
            </button>
          }
          onComplete={(account) => {
            onComplete?.(account)
            onOpenChange(false)
          }}
        />
      </SheetContent>
    </Sheet>
  )
}
